import { HttpStatusCode } from 'axios';
import { errorReason, errorStatus, stringifyError } from '@srm/core/errors';
import { BackgroundJobStatus, IBackgroundJob, IBackgroundJobStarted } from '@models/backgroundJobs/IBackgroundJob';
import { backgroundJobsStore, BackgroundJobsStore } from './BackgroundJobsStore';
import { isJobActive } from './backgroundJobsReducer';

/** Список задач несёт только число замечаний: сами замечания итога окно дочитывает `GET jobs/{id}`. */
const lacksWarnings = (job: IBackgroundJob): boolean => job.warnings == null && (job.warningsTotal ?? 0) > 0;

/**
 * Окно дочитывает свою задачу чаще страховочного опроса колокольчика: место в очереди пушем не приходит, а пуш задач
 * на сервере выключается отдельно от хаба.
 */
export const JOB_POLL_MS = 5000;

/** Отказы старта, у которых сервер может не назвать причину (ответ прокси, переполненная очередь). */
const START_FAILURE_TEXTS: Partial<Record<number, string>> = {
    [HttpStatusCode.Forbidden]: 'Недостаточно прав для этого действия',
    [HttpStatusCode.PayloadTooLarge]: 'Файл слишком большой',
    [HttpStatusCode.TooManyRequests]: 'Уже идут фоновые задачи — дождитесь их окончания',
    [HttpStatusCode.ServiceUnavailable]: 'Сервер сейчас загружен — попробуйте позже',
};

/** Фона нет (выключен на сервере или не разрешён кнопке), а работы без задачи у окна нет. */
export const BACKGROUND_UNAVAILABLE_TEXT = 'Фоновые задачи сейчас недоступны. Обратитесь к администратору.';

/**
 * 400 старта из-за ServerOptions кнопки: фон включён только в ClientOptions. Сервер называет флаг в тексте отказа;
 * при `AppSettings:ExceptionGuidOnly` текста нет — тогда отказ показывается как есть.
 */
export const isBackgroundNotAllowed = (error: unknown): boolean =>
    errorStatus(error) === HttpStatusCode.BadRequest && /runInBackground/i.test(errorReason(error) ?? '');

const knownStartFailureText = (error: unknown): string | undefined => {
    const status = errorStatus(error);
    return status === undefined ? undefined : START_FAILURE_TEXTS[status];
};

export const backgroundStartFailureText = (error: unknown): string => {
    const known = knownStartFailureText(error);
    return known ? (errorReason(error) ?? known) : stringifyError(error);
};

/** Отказ старта для показа уведомлением: известный статус без причины сервера — понятным текстом, иначе — как есть. */
const startFailure = (error: unknown): unknown => {
    const known = knownStartFailureText(error);
    return known && !errorReason(error) ? new Error(known, { cause: error }) : error;
};

export type BackgroundJobStartResult =
    | { type: 'started'; jobId: string; status?: BackgroundJobStatus }
    /** Фона нет: механизм выключен (404) или кнопке не разрешён (400 `runInBackground`) — работать без задачи. */
    | { type: 'fallback' }
    /** `message` — текст для окна; `error` — отказ для уведомления (с кодом обращения), если старт отклонён. */
    | { type: 'failed'; message: string; error?: unknown };

/** Поставить задачу: `start` — POST вида, отвечающий `{ jobId }`. */
export const startBackgroundJob = async (
    start: () => Promise<IBackgroundJobStarted>,
    { noJobIdText }: { noJobIdText: string },
): Promise<BackgroundJobStartResult> => {
    let started: IBackgroundJobStarted;
    try {
        started = await start();
    } catch (error) {
        if (errorStatus(error) === HttpStatusCode.NotFound || isBackgroundNotAllowed(error)) {
            return { type: 'fallback' };
        }
        return { type: 'failed', message: backgroundStartFailureText(error), error: startFailure(error) };
    }
    const jobId = started?.jobId;
    if (typeof jobId !== 'string' || !jobId.trim()) return { type: 'failed', message: noJobIdText };
    return { type: 'started', jobId, status: started.status };
};

export type BackgroundJobWaitStore = Pick<
    BackgroundJobsStore,
    'track' | 'subscribeJob' | 'observe' | 'refreshJob' | 'downloadFile' | 'markShownInWindow'
>;

export interface IAwaitBackgroundJobOptions {
    /** Окно закрыто или ушло в фон: перестать следить, задачу не трогать. Промис тогда не завершается. */
    signal: AbortSignal;
    /** Каждое новое состояние идущей задачи. */
    onUpdate?: (job: IBackgroundJob) => void;
    store?: BackgroundJobWaitStore;
}

/**
 * Дождаться итога задачи, пока окно открыто: окно наблюдает её (`observe` — шапка не звонит о её итоге, `watch` —
 * уведомление не уходит, пока окно ждёт) и дочитывает из видимой вкладки. Итог — закончившаяся задача (итог,
 * известный из списка без замечаний, — дочитанный); `null` — задачи больше нет. Окно закрыли (`signal`) — наблюдение снимается, задача дорабатывает, итог придёт тостом и
 * уведомлением, а промис не завершается.
 */
export const awaitBackgroundJob = (
    jobId: string,
    { signal, onUpdate, store = backgroundJobsStore }: IAwaitBackgroundJobOptions,
): Promise<IBackgroundJob | null> => {
    // Задача — сразу в колокольчике: когда окно уйдёт, её ход и итог покажет он. Механизм, сочтённый выключенным
    // (404 списка), заодно перепроверяется — иначе после ухода окна задачу никто бы не дочитывал.
    store.track({ jobId });
    // Окно закрыли, пока шёл старт: ждать задачу некому.
    if (signal.aborted) return new Promise<IBackgroundJob | null>(() => {});

    const release = store.observe(jobId);

    return new Promise<IBackgroundJob | null>((resolve) => {
        let done = false;
        const poll = window.setInterval(() => {
            if (document.visibilityState !== 'hidden') void store.refreshJob(jobId);
        }, JOB_POLL_MS);

        const stop = () => {
            done = true;
            window.clearInterval(poll);
            signal.removeEventListener('abort', onAbort);
        };
        const onAbort = () => {
            stop();
            unsubscribe();
            release();
        };
        const finish = (job: IBackgroundJob | null) => {
            stop();
            // Колокольчик решает, звонить ли об итоге, в том же обходе подписчиков, что и окно (а известный итог
            // приходит ещё внутри subscribeJob): подписка и наблюдение снимаются после него, иначе итог перед
            // глазами пришёл бы ещё и тостом.
            queueMicrotask(() => {
                unsubscribe();
                release();
            });
            if (job && lacksWarnings(job)) {
                void store.refreshJob(jobId).then((fresh) => resolve(fresh ?? job));
            } else {
                resolve(job);
            }
        };

        signal.addEventListener('abort', onAbort);
        const unsubscribe = store.subscribeJob(jobId, (job) => {
            if (done) return;
            if (job === null) finish(null);
            else if (isJobActive(job)) onUpdate?.(job);
            else finish(job);
        });
    });
};
